import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { loadProducts, loadInsurance, loadProductPricing, updateProductPricing } from '../BaseURL';
import Swal from 'sweetalert2';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Button,
  CircularProgress,
  Alert,
  Backdrop,
  Snackbar,
  Divider,
  Chip,
  Stack,
  TablePagination,
  Grid,
  Checkbox
} from '@mui/material';
import {
  PriceChange as PriceIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  LocalPharmacy as LocalPharmacyIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  SelectAll as SelectAllIcon,
  Deselect as DeselectIcon
} from '@mui/icons-material';

const ProductPricing = () => {
  // Products state
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [error, setError] = useState(null);
  const [storProNames, setStorProNames] = useState([]);

  // Insurance state
  const [insuranceList, setInsuranceList] = useState([]);
  const [productPricing, setProductPricing] = useState({});
  const [editingPricing, setEditingPricing] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [savingPrices, setSavingPrices] = useState(false);
  const [loadingPricing, setLoadingPricing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [applyAllTopPrice, setApplyAllTopPrice] = useState('');
  const [applyAllTariff, setApplyAllTariff] = useState('');
  const [editingErrors, setEditingErrors] = useState({});
  const [productCode, setProductCode] = useState('');
  const [originalCode, setOriginalCode] = useState('');
  const lastApplyPrevRef = useRef(null);

  // NEW: Row selection state
  const [selectedRows, setSelectedRows] = useState(new Set());

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Load products & insurance & store_pro names
  useEffect(() => {
    const loadProductData = async () => {
      try {
        setLoadingProducts(true);
        const response = await axios.get(loadProducts);
        if (response.data.success) {
          setProducts(response.data.results);
        }
      } catch (error) {
        console.error('Error loading products:', error);
        setError('Failed to load products');
      } finally {
        setLoadingProducts(false);
      }
    };

    const loadInsuranceData = async () => {
      try {
        const response = await axios.get(loadInsurance);
        if (response.data.success) {
          setInsuranceList(response.data.results);
        }
      } catch (error) {
        console.error('Error loading insurance:', error);
      }
    };

    const loadStorProNames = async () => {
      try {
        const response = await axios.get('/api/store-pricing/product-names');
        if (response.data && response.data.success && Array.isArray(response.data.names)) {
          setStorProNames(response.data.names);
        }
      } catch (error) {
        console.error('Error loading store_pro product names:', error);
      }
    };

    loadProductData();
    loadInsuranceData();
    loadStorProNames();
  }, []);

  // Filter products: only those in store_pro, then apply search
  useEffect(() => {
    var filtered = products.filter(function(product) {
      if (!product.name) return false;
      // Only include if product exists in store_pro
      if (storProNames.length > 0 && storProNames.indexOf(product.name) === -1) return false;
      // Apply search term
      if (searchTerm && product.name.toLowerCase().indexOf(searchTerm.toLowerCase()) === -1) return false;
      return true;
    });
    setFilteredProducts(filtered);
    setPage(0);
  }, [searchTerm, products, storProNames]);

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Load pricing when product is selected
  useEffect(() => {
    if (selectedProduct && insuranceList.length > 0) {
      setProductCode(selectedProduct.code || '');
      setOriginalCode(selectedProduct.code || '');
      fetchProductPricing();
    }
  }, [selectedProduct, insuranceList]);

  // Reset selection when product changes or edit mode toggles
  useEffect(() => {
    setSelectedRows(new Set());
  }, [selectedProduct, isEditing]);

  const handlePriceChange = (insuranceId, field, value) => {
    const str = value === null ? '' : String(value);
    let error = '';
    if (str !== '') {
      const num = Number(str);
      if (Number.isNaN(num)) error = 'Invalid number';
      else if (num < 0) error = 'Must be >= 0';
    }

    setEditingErrors(prev => ({
      ...prev,
      [insuranceId]: { ...prev[insuranceId], [field]: error }
    }));

    setEditingPricing(prev => ({
      ...prev,
      [insuranceId]: { ...prev[insuranceId], [field]: str }
    }));
  };

const fetchProductPricing = async () => {
  setLoadingPricing(true);

  try {
    // POST + JSON body: the name travels in the request body, so characters
    // like '&', '/', '+', '#', '%' are immune to URL / WAF / proxy quirks.
    // 20 s timeout keeps the spinner from getting stuck on a hung connection.
    const { data } = await axios.post(
      loadProductPricing,
      { name: String(selectedProduct.name || '').trim() },
      { timeout: 20000 }
    );

    const pricing = {};

    insuranceList.forEach(ins => {
      const existing = data?.pricing?.find(
        p => Number(p.insurance_id) === Number(ins.id)
      );

      pricing[ins.id] = {
        insuranceId: ins.id,
        insuranceName: ins.name,
        topPrice: existing?.top_price != null ? Number(existing.top_price) : null,
        tariff: existing?.cost != null ? Number(existing.cost) : null,
        code: existing?.code ?? "",
        unit: existing?.unit ?? "",
        status: existing?.status || "not found"
      };
    });

    setProductPricing(pricing);

    const editing = Object.fromEntries(
      Object.entries(pricing).map(([k, v]) => [
        k,
        {
          ...v,
          topPrice: v.topPrice ?? "",
          tariff: v.tariff ?? ""
        }
      ])
    );

    setEditingPricing(editing);
    setIsEditing(false);

  } catch (err) {
    console.error("Error loading pricing:", err);

    const empty = Object.fromEntries(
      insuranceList.map(ins => [
        ins.id,
        {
          insuranceId: ins.id,
          insuranceName: ins.name,
          topPrice: null,
          tariff: null,
          code: "",
          unit: "",
          status: "not found"
        }
      ])
    );

    setProductPricing(empty);

    setEditingPricing(
      Object.fromEntries(
        Object.entries(empty).map(([k, v]) => [
          k,
          { ...v, topPrice: "", tariff: "" }
        ])
      )
    );

    setIsEditing(false);

  } finally {
    setLoadingPricing(false);
  }
};

  const handleSavePricing = async () => {
    setSavingPrices(true);
    try {
      const hasErrors = Object.values(editingErrors).some(err =>
        err && (err.topPrice || err.tariff)
      );
      if (hasErrors) {
        Swal.fire({ title: 'Fix errors', text: 'Please fix validation errors before saving', icon: 'warning' });
        setSavingPrices(false);
        return;
      }

      const pricingChanges = [];

      Object.entries(editingPricing).forEach(([insuranceId, edited]) => {
        const orig = productPricing[insuranceId];

        // Current edited values
        const editedTopPrice = edited.topPrice === '' ? null : Number(edited.topPrice);
        const editedTariff = edited.tariff === '' ? null : Number(edited.tariff);
        const editedCode = edited.code || '';
        const editedUnit = edited.unit || '';

        // Original values from backend
        const origTopPrice = orig && orig.topPrice !== null ? Number(orig.topPrice) : null;
        const origTariff = orig && orig.tariff !== null ? Number(orig.tariff) : null;
        const origCode = orig ? (orig.code || '') : '';
        const origUnit = orig ? (orig.unit || '') : '';

        // Detect any change across all four fields
        const topPriceChanged = editedTopPrice !== origTopPrice &&
          !(editedTopPrice === null && origTopPrice === null);
        const tariffChanged = editedTariff !== origTariff &&
          !(editedTariff === null && origTariff === null);
        const codeChanged = editedCode !== origCode;
        const unitChanged = editedUnit !== origUnit;

        if (topPriceChanged || tariffChanged || codeChanged || unitChanged) {
          pricingChanges.push({
            insuranceName: edited.insuranceName,
            topPrice: editedTopPrice !== null ? editedTopPrice : (origTopPrice !== null ? origTopPrice : 0),
            cost: editedTariff !== null ? editedTariff : (origTariff !== null ? origTariff : 0),
            code: editedCode,
            unit: editedUnit
          });
        }
      });

      if (pricingChanges.length === 0 && productCode === originalCode) {
        Swal.fire({ title: 'No changes', text: 'No new prices to save', icon: 'info' });
        setSavingPrices(false);
        return;
      }

      const changesHtml = pricingChanges.map(c =>
        '<div style="text-align:left;margin:4px 0;padding:6px 10px;background:#f0fdf4;border-radius:6px;border-left:3px solid #41a386;">' +
          '<strong style="color:#1e293b">' + c.insuranceName + '</strong><br/>' +
          '<span style="color:#64748b;font-size:13px;">' +
            'Topup Price: <strong style="color:#41a386">GHS ' + Number(c.topPrice).toFixed(2) + '</strong>' +
            ' &nbsp;|&nbsp; Tariff: <strong style="color:#0ea5e9">GHS ' + Number(c.cost).toFixed(2) + '</strong>' +
            (c.code ? ' &nbsp;|&nbsp; Code: <strong style="color:#92400e">' + c.code + '</strong>' : '') +
            (c.unit ? ' &nbsp;|&nbsp; Unit: <strong style="color:#7c3aed">' + c.unit + '</strong>' : '') +
          '</span>' +
        '</div>'
      ).join('');

      const codeChangeHtml = productCode !== originalCode
        ? `<div style="text-align:left;margin:4px 0 8px;padding:6px 10px;background:#e0f2fe;border-radius:6px;border-left:3px solid #0ea5e9;">
            <strong style="color:#1e293b">Product Code</strong><br/>
            <span style="color:#64748b;font-size:13px;">${originalCode || '(none)'} &nbsp;→&nbsp; <strong style="color:#0ea5e9">${productCode || '(cleared)'}</strong></span>
          </div>`
        : '';

      const escapeHtml = (s) => String(s ?? '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
      const confirmResult = await Swal.fire({
        title: 'Confirm Pricing Update',
        html: `<p style="color:#64748b;margin-bottom:12px;">Updating pricing for <strong style="color:#1e293b">${escapeHtml(selectedProduct.name)}</strong>:</p>${codeChangeHtml}${changesHtml}`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#41a386',
        cancelButtonColor: '#64748b',
        confirmButtonText: 'Yes, update',
        cancelButtonText: 'Cancel'
      });

      if (!confirmResult.isConfirmed) {
        setSavingPrices(false);
        return;
      }

      const response = await axios.post(updateProductPricing, {
        productName: selectedProduct.name,
        productCode: productCode,
        pricing: pricingChanges
      });

      if (response.data && response.data.success) {
        // Rebuild productPricing from the current editingPricing (source of truth after edits)
        var updated = {};
        Object.keys(editingPricing).forEach(function(id) {
          var ed = editingPricing[id];
          updated[id] = {
            insuranceId: ed.insuranceId,
            insuranceName: ed.insuranceName,
            topPrice: ed.topPrice === '' ? null : Number(ed.topPrice),
            tariff: ed.tariff === '' ? null : Number(ed.tariff),
            code: ed.code || '',
            unit: ed.unit || '',
            status: 'active'
          };
        });
        setProductPricing(updated);

        // Sync originalCode to saved value
        setOriginalCode(productCode);

        var synced = {};
        Object.keys(updated).forEach(function(k) {
          var v = updated[k];
          synced[k] = {
            insuranceId: v.insuranceId,
            insuranceName: v.insuranceName,
            topPrice: v.topPrice !== null ? v.topPrice : '',
            tariff: v.tariff !== null ? v.tariff : '',
            code: v.code || '',
            unit: v.unit || '',
            status: v.status
          };
        });
        setEditingPricing(synced);
        setIsEditing(false);
        setSnackbar({ open: true, message: 'Pricing updated successfully', severity: 'success', showUndo: false });
      } else {
        throw new Error((response.data && response.data.message) || 'Failed to save pricing');
      }
    } catch (error) {
      console.error('Error saving pricing:', error);
      Swal.fire({
        title: 'Error!',
        text: (error.response && error.response.data && error.response.data.message) || error.message || 'Failed to save product pricing',
        icon: 'error'
      });
    } finally {
      setSavingPrices(false);
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // NEW: Modified bulk update functions to only apply to selected rows
  const handleApplyTopPriceToAll = () => {
    if (selectedRows.size === 0) {
      Swal.fire({ 
        title: 'No rows selected', 
        text: 'Please select at least one insurance row to apply the topup price', 
        icon: 'info' 
      });
      return;
    }
    
    if (applyAllTopPrice === '') {
      Swal.fire({ title: 'Enter a price', text: 'Please enter a topup price to apply', icon: 'info' });
      return;
    }
    const num = Number(applyAllTopPrice);
    if (Number.isNaN(num) || num < 0) {
      Swal.fire({ title: 'Invalid price', text: 'Please enter a valid non-negative number', icon: 'error' });
      return;
    }
    setEditingPricing(prev => {
      lastApplyPrevRef.current = prev;
      const updated = { ...prev };
      selectedRows.forEach(insuranceId => {
        if (updated[insuranceId]) {
          updated[insuranceId] = { ...updated[insuranceId], topPrice: String(num) };
        }
      });
      setEditingErrors({});
      return updated;
    });
    setSnackbar({ 
      open: true, 
      message: `Applied topup price to ${selectedRows.size} selected insurance(s)`, 
      severity: 'info', 
      showUndo: true 
    });
  };

  const handleApplyTariffToAll = () => {
    if (selectedRows.size === 0) {
      Swal.fire({ 
        title: 'No rows selected', 
        text: 'Please select at least one insurance row to apply the tariff', 
        icon: 'info' 
      });
      return;
    }
    
    if (applyAllTariff === '') {
      Swal.fire({ title: 'Enter a tariff', text: 'Please enter a tariff to apply', icon: 'info' });
      return;
    }
    const num = Number(applyAllTariff);
    if (Number.isNaN(num) || num < 0) {
      Swal.fire({ title: 'Invalid tariff', text: 'Please enter a valid non-negative number', icon: 'error' });
      return;
    }
    setEditingPricing(prev => {
      lastApplyPrevRef.current = prev;
      const updated = { ...prev };
      selectedRows.forEach(insuranceId => {
        if (updated[insuranceId]) {
          updated[insuranceId] = { ...updated[insuranceId], tariff: String(num) };
        }
      });
      setEditingErrors({});
      return updated;
    });
    setSnackbar({ 
      open: true, 
      message: `Applied tariff to ${selectedRows.size} selected insurance(s)`, 
      severity: 'info', 
      showUndo: true 
    });
  };

  const handleUndoApply = () => {
    if (lastApplyPrevRef.current) {
      setEditingPricing(lastApplyPrevRef.current);
      lastApplyPrevRef.current = null;
      setSnackbar({ open: true, message: 'Reverted applied price', severity: 'info', showUndo: false });
    }
  };

  // NEW: Row selection handlers
  const handleToggleRow = (insuranceId) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(insuranceId)) {
        newSet.delete(insuranceId);
      } else {
        newSet.add(insuranceId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    const allIds = Object.keys(editingPricing);
    setSelectedRows(new Set(allIds));
  };

  const handleDeselectAll = () => {
    setSelectedRows(new Set());
  };

  const allSelected = useMemo(() => {
    const allIds = Object.keys(editingPricing);
    return allIds.length > 0 && allIds.every(id => selectedRows.has(id));
  }, [editingPricing, selectedRows]);

  const someSelected = useMemo(() => {
    return selectedRows.size > 0 && !allSelected;
  }, [selectedRows, allSelected]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // UX helpers
  const isValidApplyTopPrice = applyAllTopPrice !== '' &&
    !Number.isNaN(Number(applyAllTopPrice)) &&
    Number(applyAllTopPrice) >= 0;

  const isValidApplyTariff = applyAllTariff !== '' &&
    !Number.isNaN(Number(applyAllTariff)) &&
    Number(applyAllTariff) >= 0;

  const hasErrors = Object.values(editingErrors).some(err =>
    err && (err.topPrice || err.tariff)
  );

  const hasChanges = useMemo(() => {
    if (!selectedProduct) return false;
    // Detect product code change
    if (productCode !== originalCode) return true;
    for (var insuranceId in editingPricing) {
      var edited = editingPricing[insuranceId];
      var orig = productPricing[insuranceId];
      var editedTopPrice = edited.topPrice === '' ? null : Number(edited.topPrice);
      var editedTariff = edited.tariff === '' ? null : Number(edited.tariff);
      var origTopPrice = orig && orig.topPrice !== null ? Number(orig.topPrice) : null;
      var origTariff = orig && orig.tariff !== null ? Number(orig.tariff) : null;

      if (editedTopPrice !== null && !Number.isNaN(editedTopPrice)) {
        if (origTopPrice === null || Number(origTopPrice) !== Number(editedTopPrice)) return true;
      }
      if (editedTariff !== null && !Number.isNaN(editedTariff)) {
        if (origTariff === null || Number(origTariff) !== Number(editedTariff)) return true;
      }
      // Detect code / unit changes per insurance row
      var origCode = orig ? (orig.code || '') : '';
      var origUnit = orig ? (orig.unit || '') : '';
      if ((edited.code || '') !== origCode) return true;
      if ((edited.unit || '') !== origUnit) return true;
    }
    return false;
  }, [selectedProduct, editingPricing, productPricing, productCode, originalCode]);

 

  if (loadingProducts) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3
      }}>
        <CircularProgress size={60} sx={{ color: '#41a386' }} />
        <Typography variant="h6" sx={{ mt: 3, fontWeight: 500, color: '#1e293b' }}>
          Loading Products...
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: '#64748b' }}>
          Please wait while we fetch product data
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        p: 3
      }}>
        <Alert severity="error" sx={{ maxWidth: 500, width: '100%', borderRadius: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  // ── Theme tokens (mirrors Home.js) ─────────────────────────────────────
  const PRIMARY      = '#41a386';
  const PRIMARY_DARK = '#2f8068';
  const PRIMARY_BG   = 'rgba(65,163,134,0.10)';
  const BORDER       = '#e5e9ef';
  const MUTED        = '#64748b';
  const STRONG       = '#0f172a';
  const LABEL_SX = {
    fontSize: '0.66rem', fontWeight: 700, color: '#94a3b8',
    textTransform: 'uppercase', letterSpacing: '0.6px',
  };

  // Small derived stats for the header strip
  const configuredCount = Object.entries(editingPricing).filter(([id]) => {
    const p = productPricing[id];
    return p && (p.topPrice !== null || p.tariff !== null);
  }).length;

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8fafc', minHeight: '100vh', py: 2.5, px: 3 }}>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            bgcolor: snackbar.severity === 'success' ? '#41a386' : snackbar.severity === 'info' ? '#0ea5e9' : '#f59e0b',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }
        }}
        message={
          <Stack direction="row" spacing={1} alignItems="center">
            <CheckCircleIcon sx={{ fontSize: 18 }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>{snackbar.message}</Typography>
          </Stack>
        }
        action={snackbar.showUndo ? (
          <Button color="inherit" size="small" onClick={handleUndoApply} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Undo
          </Button>
        ) : null}
      />

      {/* Backdrop loader */}
      <Backdrop
        open={loadingPricing}
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={48} color="inherit" />
          <Typography variant="body1" sx={{ fontWeight: 500 }}>Loading prices...</Typography>
        </Box>
      </Backdrop>

      {/* Page Header — Home.js style */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        spacing={1.5}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography sx={{ ...LABEL_SX, color: PRIMARY, mb: 0.25 }}>
            Procurement · Pricing
          </Typography>
          <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
            Product Pricing
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', color: MUTED, mt: 0.25 }}>
            {filteredProducts.length} of {products.length} product{products.length === 1 ? '' : 's'} ·
            {' '}{insuranceList.length} insurance partner{insuranceList.length === 1 ? '' : 's'}
            {selectedProduct ? ` · ${configuredCount} configured for "${selectedProduct.name}"` : ''}
          </Typography>
        </Box>
      </Stack>

      {/* Stats strip */}
      <Grid container spacing={1.25} sx={{ mb: 2 }}>
        {[
          { label: 'Products',          value: products.length,             icon: <LocalPharmacyIcon fontSize="small" />, tint: '#f1f5f9',                color: STRONG    },
          { label: 'Insurance Partners',value: insuranceList.length,        icon: <PriceIcon         fontSize="small" />, tint: 'rgba(14,165,233,0.10)',  color: '#0369A1' },
          { label: 'Configured (this)', value: selectedProduct ? configuredCount : 0, icon: <CheckCircleIcon  fontSize="small" />, tint: 'rgba(16,185,129,0.10)',  color: '#15803D' },
          { label: 'Mode',              value: isEditing ? 'Editing' : 'View', display: true, icon: <EditIcon fontSize="small" />, tint: isEditing ? PRIMARY_BG : 'rgba(217,119,6,0.10)', color: isEditing ? PRIMARY_DARK : '#A16207' },
        ].map(t => (
          <Grid item xs={6} md={3} key={t.label}>
            <Paper elevation={0} sx={{
              p: 1.5, borderRadius: 2, border: `1px solid ${BORDER}`, bgcolor: '#fff',
              display: 'flex', alignItems: 'center', gap: 1.5,
            }}>
              <Box sx={{
                width: 36, height: 36, borderRadius: 1.5, bgcolor: t.tint, color: t.color,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                {t.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={LABEL_SX}>{t.label}</Typography>
                <Typography sx={{ fontSize: t.display ? '1rem' : '1.35rem', fontWeight: 800, color: STRONG, lineHeight: 1.1, mt: 0.25 }} noWrap>
                  {t.display ? t.value : Number(t.value || 0).toLocaleString()}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

    

      {/* Main Layout: Left product list + Right pricing panel */}
      <Box sx={{
        display: 'flex',
        gap: 3,
        minHeight: 'calc(100vh - 280px)',
        flexDirection: { xs: 'column', lg: 'row' }
      }}>

        {/* ─── Left Panel: Product List ─── */}
        <Box sx={{ width: { xs: '100%', lg: '30%' }, minWidth: 0 }}>
          <Paper elevation={0} sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            border: `1px solid ${BORDER}`,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}>
            {/* Search header */}
            <Box sx={{ p: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: '#fff' }}>
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ mb: 1.25 }}>
                <Box sx={{
                  width: 32, height: 32, borderRadius: 1.25,
                  bgcolor: PRIMARY_BG, color: PRIMARY,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <LocalPharmacyIcon sx={{ fontSize: 18 }} />
                </Box>
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography sx={LABEL_SX}>Catalogue</Typography>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: STRONG, lineHeight: 1.2 }}>
                    Products
                  </Typography>
                </Box>
                <Box sx={{
                  px: 0.85, py: 0.25, borderRadius: 0.85,
                  bgcolor: '#f1f5f9', color: MUTED,
                  fontSize: '0.7rem', fontWeight: 700,
                }}>
                  {filteredProducts.length}
                </Box>
              </Stack>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="Search products…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#94a3b8', fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 1.5,
                    bgcolor: '#f8fafc',
                  },
                }}
              />
            </Box>

            {/* Product table */}
            <TableContainer sx={{ flexGrow: 1, maxHeight: '55vh' }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', bgcolor: '#f8fafc', py: 1.5 }}>
                      Name
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px', bgcolor: '#f8fafc', py: 1.5 }}>
                      Type
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredProducts
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((product) => {
                      const isSelected = selectedProduct && selectedProduct.name === product.name;
                      return (
                        <TableRow
                          key={product.name}
                          onClick={() => setSelectedProduct(product)}
                          sx={{
                            cursor: 'pointer',
                            borderLeft: isSelected ? '4px solid #41a386' : '4px solid transparent',
                            bgcolor: isSelected ? '#f0fdf4' : 'white',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              bgcolor: isSelected ? '#f0fdf4' : '#f8fafc'
                            }
                          }}
                        >
                          <TableCell sx={{ fontWeight: isSelected ? 600 : 500, fontSize: '0.82rem', color: isSelected ? '#41a386' : '#1e293b', py: 1.2 }}>
                            {product.name}
                          </TableCell>
                          <TableCell sx={{ py: 1.2 }}>
                            <Chip
                              label={product.types ? product.types.split(',').join(', ') : 'N/A'}
                              size="small"
                              sx={{
                                bgcolor: (product.types && product.types.includes('drug')) ? '#e0f2fe' : '#fef3c7',
                                color: (product.types && product.types.includes('drug')) ? '#0369a1' : '#92400e',
                                fontWeight: 500,
                                fontSize: '0.7rem'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {filteredProducts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={2} sx={{ textAlign: 'center', py: 6 }}>
                        <SearchIcon sx={{ fontSize: 40, color: '#cbd5e1', mb: 1 }} />
                        <Typography variant="body2" sx={{ color: '#64748b' }}>No products found</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />
            <TablePagination
              component="div"
              count={filteredProducts.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                bgcolor: '#f8fafc',
                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                  color: '#64748b',
                  fontWeight: 500,
                  fontSize: '0.78rem'
                }
              }}
            />
          </Paper>
        </Box>

        {/* ─── Right Panel: Pricing Table ─── */}
        <Box sx={{ width: { xs: '100%', lg: '70%' }, minWidth: 0 }}>
          <Paper elevation={0} sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
            border: `1px solid ${BORDER}`,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}>

            {selectedProduct ? (
              <>
                {/* Selected product header */}
                <Box sx={{
                  p: 1.75,
                  bgcolor: '#fff',
                  borderBottom: `1px solid ${BORDER}`
                }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={1.5}>
                    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: 1.5,
                        bgcolor: PRIMARY_BG, color: PRIMARY,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}>
                        <PriceIcon sx={{ fontSize: 20 }} />
                      </Box>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={LABEL_SX}>Selected product</Typography>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: STRONG, lineHeight: 1.2 }} noWrap>
                            {selectedProduct.name}
                          </Typography>
                          {loadingPricing && <CircularProgress size={14} sx={{ color: PRIMARY }} />}
                        </Stack>
                        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.4, flexWrap: 'wrap', rowGap: 0.5 }}>
                          <Box sx={{
                            px: 0.7, py: 0.15, borderRadius: 0.7, fontSize: '0.65rem', fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '0.4px',
                            bgcolor: (selectedProduct.types || '').includes('drug') ? 'rgba(14,165,233,0.10)' : 'rgba(245,158,11,0.10)',
                            color:   (selectedProduct.types || '').includes('drug') ? '#0369A1' : '#92400E',
                          }}>
                            {selectedProduct.types ? selectedProduct.types.split(',').join(', ') : 'N/A'}
                          </Box>
                          <Box sx={{
                            px: 0.7, py: 0.15, borderRadius: 0.7, fontSize: '0.65rem', fontWeight: 700,
                            textTransform: 'uppercase', letterSpacing: '0.4px',
                            bgcolor: 'rgba(14,165,233,0.10)', color: '#0369A1',
                          }}>
                            {configuredCount} insurance{configuredCount === 1 ? '' : 's'}
                          </Box>
                        </Stack>
                      </Box>
                    </Stack>

                    {/* Product Code row */}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
                      <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500, minWidth: 42 }}>
                        Code:
                      </Typography>
                      {isEditing ? (
                        <TextField
                          size="small"
                          value={productCode}
                          onChange={(e) => setProductCode(e.target.value)}
                          placeholder="e.g. MED-001"
                          sx={{
                            width: 180,
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              bgcolor: '#f8fafc',
                              fontSize: '0.82rem',
                              '&:hover': { bgcolor: '#f1f5f9' },
                              '&.Mui-focused': { bgcolor: 'white' }
                            }
                          }}
                        />
                      ) : (
                        productCode ? (
                          <Chip
                            label={productCode}
                            size="small"
                            sx={{
                              bgcolor: '#fef3c7',
                              color: '#92400e',
                              fontWeight: 600,
                              fontSize: '0.75rem'
                            }}
                          />
                        ) : (
                          <Chip
                            label="No code"
                            size="small"
                            sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', fontSize: '0.72rem' }}
                          />
                        )
                      )}
                    </Stack>

                    {/* Action buttons */}
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      {!isEditing ? (
                        <Button
                          variant="contained"
                          startIcon={<EditIcon />}
                          onClick={() => setIsEditing(true)}
                          sx={{
                            bgcolor: PRIMARY,
                            '&:hover': { bgcolor: PRIMARY_DARK },
                            textTransform: 'none',
                            fontWeight: 700,
                            boxShadow: 'none',
                            px: 2.5,
                          }}
                        >
                          Edit Pricing
                        </Button>
                      ) : (
                        <>
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={savingPrices ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <SaveIcon sx={{ fontSize: 18 }} />}
                            onClick={handleSavePricing}
                            disabled={savingPrices || !hasChanges || hasErrors}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 700,
                              bgcolor: PRIMARY,
                              '&:hover': { bgcolor: PRIMARY_DARK },
                              '&:disabled': { bgcolor: '#e2e8f0', color: '#94a3b8' },
                              boxShadow: 'none',
                              px: 2.5,
                            }}
                          >
                            {savingPrices ? 'Saving…' : 'Save'}
                          </Button>
                          <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={() => {
                              const synced = Object.keys(productPricing).reduce(function(acc, k) {
                                var v = productPricing[k];
                                acc[k] = {
                                  ...v,
                                  topPrice: v.topPrice !== null ? v.topPrice : '',
                                  tariff: v.tariff !== null ? v.tariff : ''
                                };
                                return acc;
                              }, {});
                              setEditingPricing(synced);
                              setProductCode(originalCode);
                              setIsEditing(false);
                              setEditingErrors({});
                              setApplyAllTopPrice('');
                              setApplyAllTariff('');
                              lastApplyPrevRef.current = null;
                            }}
                            disabled={savingPrices}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 600,
                              color: '#64748b',
                              borderColor: '#cbd5e1',
                              '&:hover': { bgcolor: '#f1f5f9' }
                            }}
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </Stack>
                  </Stack>
                </Box>

                {/* Bulk apply bar — visible only in edit mode */}
                {isEditing && (
                  <Box sx={{
                    px: 3,
                    py: 2,
                    bgcolor: '#f0fdf4',
                    borderBottom: '1px solid #bbf7d0'
                  }}>
                    {/* Selection controls */}
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                      <InfoIcon sx={{ fontSize: 18, color: '#41a386' }} />
                      <Typography variant="body2" sx={{ color: '#166534', fontWeight: 600 }}>
                        {selectedRows.size} row{selectedRows.size !== 1 ? 's' : ''} selected
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          startIcon={<SelectAllIcon />}
                          onClick={handleSelectAll}
                          disabled={allSelected}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            color: '#41a386',
                            '&:hover': { bgcolor: 'rgba(65,163,134,0.08)' }
                          }}
                        >
                          Select All
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DeselectIcon />}
                          onClick={handleDeselectAll}
                          disabled={selectedRows.size === 0}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            color: '#64748b',
                            '&:hover': { bgcolor: 'rgba(100,116,139,0.08)' }
                          }}
                        >
                          Deselect All
                        </Button>
                      </Stack>
                    </Stack>

                    {/* Bulk update controls */}
                    <Box sx={{
                      display: 'flex',
                      gap: 2,
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      <Typography variant="body2" sx={{ color: '#166534', fontWeight: 500 }}>
                        Bulk Update:
                      </Typography>

                      {/* Top Price apply */}
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Topup price"
                        value={applyAllTopPrice}
                        onChange={(e) => setApplyAllTopPrice(e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{
                          width: 130,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: 'white',
                            fontSize: '0.82rem'
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={handleApplyTopPriceToAll}
                        size="small"
                        disabled={!isValidApplyTopPrice || selectedRows.size === 0}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#41a386',
                          color: '#41a386',
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          '&:hover': { bgcolor: 'rgba(65,163,134,0.08)' },
                          '&:disabled': { borderColor: '#cbd5e1', color: '#94a3b8' }
                        }}
                      >
                        Apply to Selected
                      </Button>

                      <Divider orientation="vertical" flexItem sx={{ mx: 1, height: 30 }} />

                      {/* Tariff apply */}
                      <TextField
                        size="small"
                        type="number"
                        placeholder="Tariff"
                        value={applyAllTariff}
                        onChange={(e) => setApplyAllTariff(e.target.value)}
                        inputProps={{ min: 0, step: 0.01 }}
                        sx={{
                          width: 130,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            bgcolor: 'white',
                            fontSize: '0.82rem'
                          }
                        }}
                      />
                      <Button
                        variant="outlined"
                        onClick={handleApplyTariffToAll}
                        size="small"
                        disabled={!isValidApplyTariff || selectedRows.size === 0}
                        sx={{
                          textTransform: 'none',
                          borderColor: '#0ea5e9',
                          color: '#0ea5e9',
                          fontWeight: 600,
                          fontSize: '0.78rem',
                          '&:hover': { bgcolor: 'rgba(14,165,233,0.08)' },
                          '&:disabled': { borderColor: '#cbd5e1', color: '#94a3b8' }
                        }}
                      >
                        Apply to Selected
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Pricing Table */}
                <TableContainer sx={{ flexGrow: 1 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {/* NEW: Checkbox column header */}
                        {isEditing && (
                          <TableCell 
                            padding="checkbox"
                            sx={{
                              bgcolor: '#f8fafc',
                              py: 1.5
                            }}
                          >
                            <Checkbox
                              checked={allSelected}
                              indeterminate={someSelected}
                              onChange={() => allSelected ? handleDeselectAll() : handleSelectAll()}
                              sx={{
                                color: '#41a386',
                                '&.Mui-checked': { color: '#41a386' },
                                '&.MuiCheckbox-indeterminate': { color: '#41a386' }
                              }}
                            />
                          </TableCell>
                        )}
                        <TableCell sx={{
                          fontWeight: 600,
                          color: '#475569',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          bgcolor: '#f8fafc',
                          py: 1.5
                        }}>
                          Insurance Provider
                        </TableCell>
                        <TableCell sx={{
                          fontWeight: 600,
                          color: '#475569',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          bgcolor: '#f8fafc',
                          py: 1.5
                        }}>
                          Code
                        </TableCell>
                        <TableCell sx={{
                          fontWeight: 600,
                          color: '#475569',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          bgcolor: '#f8fafc',
                          py: 1.5
                        }}>
                          Unit
                        </TableCell>
                        <TableCell sx={{
                          fontWeight: 600,
                          color: '#475569',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          bgcolor: '#f8fafc',
                          textAlign: 'right',
                          py: 1.5
                        }}>
                          Topup Price (GHS)
                        </TableCell>
                        <TableCell sx={{
                          fontWeight: 600,
                          color: '#475569',
                          fontSize: '0.75rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          bgcolor: '#f8fafc',
                          textAlign: 'right',
                          py: 1.5
                        }}>
                          Tariff (GHS)
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(editingPricing)
                        .filter(([insuranceId, pricing]) => {
                          // Only show rows that have either top price or tariff set
                          const original = productPricing[insuranceId];
                          return (original && (original.topPrice !== null || original.tariff !== null));
                        })
                        .map(([insuranceId, pricing]) => {
                          const original = productPricing[insuranceId];
                          const hasTopPrice = original && original.topPrice !== null;
                          const hasTariff = original && original.tariff !== null;
                        const isRowSelected = selectedRows.has(insuranceId);

                        return (
                          <TableRow
                            key={insuranceId}
                            selected={isRowSelected}
                            sx={{
                              '&:hover': { bgcolor: '#f8fafc' },
                              transition: 'background-color 0.15s ease',
                              bgcolor: isRowSelected ? '#f0fdf4' : 'inherit'
                            }}
                          >
                            {/* NEW: Checkbox column */}
                            {isEditing && (
                              <TableCell padding="checkbox" sx={{ py: 1.5 }}>
                                <Checkbox
                                  checked={isRowSelected}
                                  onChange={() => handleToggleRow(insuranceId)}
                                  sx={{
                                    color: '#41a386',
                                    '&.Mui-checked': { color: '#41a386' }
                                  }}
                                />
                              </TableCell>
                            )}

                            {/* Insurance Name */}
                            <TableCell sx={{ py: 1.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                                {pricing.insuranceName}
                              </Typography>
                            </TableCell>

                            {/* Code */}
                            <TableCell sx={{ py: 1.5 }}>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  value={pricing.code || ''}
                                  onChange={function(e) {
                                    var val = e.target.value;
                                    setEditingPricing(function(prev) {
                                      var next = {};
                                      Object.keys(prev).forEach(function(k) {
                                        next[k] = k === insuranceId
                                          ? { insuranceId: prev[k].insuranceId, insuranceName: prev[k].insuranceName, topPrice: prev[k].topPrice, tariff: prev[k].tariff, code: val, unit: prev[k].unit, status: prev[k].status }
                                          : prev[k];
                                      });
                                      return next;
                                    });
                                  }}
                                  placeholder="e.g. MED-01"
                                  sx={{
                                    width: 120,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      bgcolor: '#f8fafc',
                                      fontSize: '0.82rem',
                                      '&:hover': { bgcolor: '#f1f5f9' },
                                      '&.Mui-focused': { bgcolor: 'white' }
                                    }
                                  }}
                                />
                              ) : (
                                pricing.code ? (
                                  <Chip
                                    label={pricing.code}
                                    size="small"
                                    sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, fontSize: '0.72rem' }}
                                  />
                                ) : (
                                  <Chip label="—" size="small" sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', fontSize: '0.72rem' }} />
                                )
                              )}
                            </TableCell>

                            {/* Unit */}
                            <TableCell sx={{ py: 1.5 }}>
                              {isEditing ? (
                                <TextField
                                  size="small"
                                  value={pricing.unit || ''}
                                  onChange={function(e) {
                                    var val = e.target.value;
                                    setEditingPricing(function(prev) {
                                      var next = {};
                                      Object.keys(prev).forEach(function(k) {
                                        next[k] = k === insuranceId
                                          ? { insuranceId: prev[k].insuranceId, insuranceName: prev[k].insuranceName, topPrice: prev[k].topPrice, tariff: prev[k].tariff, code: prev[k].code, unit: val, status: prev[k].status }
                                          : prev[k];
                                      });
                                      return next;
                                    });
                                  }}
                                  placeholder="e.g. Tablet"
                                  sx={{
                                    width: 120,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      bgcolor: '#f8fafc',
                                      fontSize: '0.82rem',
                                      '&:hover': { bgcolor: '#f1f5f9' },
                                      '&.Mui-focused': { bgcolor: 'white' }
                                    }
                                  }}
                                />
                              ) : (
                                pricing.unit ? (
                                  <Chip
                                    label={pricing.unit}
                                    size="small"
                                    sx={{ bgcolor: '#ede9fe', color: '#7c3aed', fontWeight: 600, fontSize: '0.72rem' }}
                                  />
                                ) : (
                                  <Chip label="—" size="small" sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', fontSize: '0.72rem' }} />
                                )
                              )}
                            </TableCell>

                            {/* Top Price */}
                            <TableCell sx={{ textAlign: 'right', py: 1.5 }}>
                              {isEditing ? (
                                <TextField
                                  type="number"
                                  value={editingPricing[insuranceId] && editingPricing[insuranceId].topPrice !== undefined && editingPricing[insuranceId].topPrice !== null ? editingPricing[insuranceId].topPrice : ''}
                                  onChange={(e) => handlePriceChange(insuranceId, 'topPrice', e.target.value)}
                                  placeholder="0.00"
                                  inputProps={{ min: 0, step: 0.01 }}
                                  size="small"
                                  error={!!(editingErrors[insuranceId] && editingErrors[insuranceId].topPrice)}
                                  helperText={(editingErrors[insuranceId] && editingErrors[insuranceId].topPrice) || ''}
                                  sx={{
                                    width: 140,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      bgcolor: '#f8fafc',
                                      fontSize: '0.85rem',
                                      '&:hover': { bgcolor: '#f1f5f9' },
                                      '&.Mui-focused': { bgcolor: 'white' }
                                    }
                                  }}
                                />
                              ) : (
                                hasTopPrice ? (
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#41a386' }}>
                                    GHS {parseFloat(original.topPrice).toFixed(2)}
                                  </Typography>
                                ) : (
                                  <Chip label="Not set" size="small" sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', fontSize: '0.72rem' }} />
                                )
                              )}
                            </TableCell>

                            {/* Tariff */}
                            <TableCell sx={{ textAlign: 'right', py: 1.5 }}>
                              {isEditing ? (
                                <TextField
                                  type="number"
                                  value={editingPricing[insuranceId] && editingPricing[insuranceId].tariff !== undefined && editingPricing[insuranceId].tariff !== null ? editingPricing[insuranceId].tariff : ''}
                                  onChange={(e) => handlePriceChange(insuranceId, 'tariff', e.target.value)}
                                  placeholder="0.00"
                                  inputProps={{ min: 0, step: 0.01 }}
                                  size="small"
                                  error={!!(editingErrors[insuranceId] && editingErrors[insuranceId].tariff)}
                                  helperText={(editingErrors[insuranceId] && editingErrors[insuranceId].tariff) || ''}
                                  sx={{
                                    width: 140,
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: 2,
                                      bgcolor: '#f8fafc',
                                      fontSize: '0.85rem',
                                      '&:hover': { bgcolor: '#f1f5f9' },
                                      '&.Mui-focused': { bgcolor: 'white' }
                                    }
                                  }}
                                />
                              ) : (
                                hasTariff ? (
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#0ea5e9' }}>
                                    GHS {parseFloat(original.tariff).toFixed(2)}
                                  </Typography>
                                ) : (
                                  <Chip label="Not set" size="small" sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', fontSize: '0.72rem' }} />
                                )
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      {Object.entries(editingPricing).filter(([insuranceId, pricing]) => {
                        const original = productPricing[insuranceId];
                        return original && (original.topPrice !== null || original.tariff !== null);
                      }).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={isEditing ? 6 : 5} sx={{ textAlign: 'center', py: 8 }}>
                            <InfoIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
                            <Typography variant="h6" sx={{ color: '#64748b', fontWeight: 500, mb: 1 }}>
                              No Pricing Data
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                              No insurance pricing has been configured for this product yet
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              /* Empty state — no product selected */
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                minHeight: 400,
                p: 6
              }}>
                <Box sx={{
                  bgcolor: '#f0fdf4',
                  p: 3,
                  borderRadius: 3,
                  mb: 3,
                  display: 'flex'
                }}>
                  <PriceIcon sx={{ fontSize: 56, color: '#41a386' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', mb: 1 }}>
                  Select a Product
                </Typography>
                <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', maxWidth: 320 }}>
                  Choose a product from the left panel to view and manage its insurance pricing
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductPricing;